import { isEqual } from 'lodash';

import { ArticleLedgerAccount } from '@work4all/models/lib/Classes/ArticleLedgerAccount.entity';
import { ArticlePrices } from '@work4all/models/lib/Classes/ArticlePrices.entity';
import { ArticleText } from '@work4all/models/lib/Classes/ArticleText.entity';
import { InputArticleLedgerAccountsAssignmentsRelation } from '@work4all/models/lib/Classes/InputArticleLedgerAccountsAssignmentsRelation.entity';
import { InputArticlePrices } from '@work4all/models/lib/Classes/InputArticlePrices.entity';
import { InputArticlePricesSetSimplePrice } from '@work4all/models/lib/Classes/InputArticlePricesSetSimplePrice.entity';
import { InputArticleRelation } from '@work4all/models/lib/Classes/InputArticleRelation.entity';
import { InputArticleSetLedgerAccountsAssignmentRelation } from '@work4all/models/lib/Classes/InputArticleSetLedgerAccountsAssignmentRelation.entity';
import { InputArticleTextAddRelation } from '@work4all/models/lib/Classes/InputArticleTextAddRelation.entity';
import { InputArticleTextModifyRelation } from '@work4all/models/lib/Classes/InputArticleTextModifyRelation.entity';
import { InputArticleTextRelation } from '@work4all/models/lib/Classes/InputArticleTextRelation.entity';

import { ArticleMaskFormValue } from '../types';

export function mapRelations(
  oldValues: ArticleMaskFormValue,
  newValues: ArticleMaskFormValue
): InputArticleRelation {
  const relations: InputArticleRelation = {};

  const articleTextsRelation = mapArticleTextsRelation(
    oldValues.articleTextList,
    newValues.articleTextList
  );

  if (articleTextsRelation) {
    relations.articleTexts = articleTextsRelation;
  }

  const articlePricesRelation = mapArticlePricesRelation(
    oldValues.articlePrices,
    newValues.articlePrices
  );

  if (articlePricesRelation) {
    relations.prices = articlePricesRelation;
  }

  const ledgerAccountAssignements = mapLedgerAccountAssignmentsRelation(
    oldValues.articleLedgerAccountAssignements,
    newValues.articleLedgerAccountAssignements
  );

  if (ledgerAccountAssignements) {
    relations.ledgerAccountAssignements = ledgerAccountAssignements;
  }

  return relations;
}

function mapArticleTextsRelation(
  oldValues: ArticleText[],
  newValues: ArticleText[]
): InputArticleTextRelation {
  const articleTextListRelation: InputArticleTextRelation = {};

  const addedText = newValues
    .filter((text) => text.id == null)
    .map((text) => {
      const input: InputArticleTextAddRelation = {
        languageCode: text.sprachCode,
        shortText: text.kurztext,
        longText: text.longtext,
      };

      return input;
    });

  if (addedText.length > 0) {
    articleTextListRelation.add = addedText;
  }

  const modifiedText = newValues
    .filter((text) => {
      if (text.id == null) return false;

      function isTextUpdated(oldText: ArticleText, newText: ArticleText) {
        return (
          oldText.sprachCode !== newText.sprachCode ||
          oldText.kurztext !== newText.kurztext ||
          oldText.longtext !== newText.longtext
        );
      }

      const oldText = oldValues.find((oldText) => oldText.id === text.id);

      return oldText != null && isTextUpdated(oldText, text);
    })
    .map((text) => {
      const input: InputArticleTextModifyRelation = {
        code: text.id,
        shortText: text.kurztext,
        longText: text.longtext,
      };

      return input;
    });

  if (modifiedText.length > 0) {
    articleTextListRelation.modify = modifiedText;
  }

  const removedText = oldValues
    .filter((text) => {
      const newText = newValues.find((newText) => newText.id === text.id);

      return newText == null;
    })
    .map((text) => text.id);

  if (removedText.length > 0) {
    articleTextListRelation.remove = removedText;
  }

  return Object.keys(articleTextListRelation).length > 0
    ? articleTextListRelation
    : null;
}

function mapArticlePricesRelation(
  oldValues: ArticlePrices,
  newValues: ArticlePrices
): InputArticlePrices {
  if (isEqual(oldValues, newValues)) {
    return null;
  }

  const articlePricesRelation: InputArticlePrices = {};

  const simplePrices = newValues.singlePriceList?.map((price) => {
    const input: InputArticlePricesSetSimplePrice = {
      priceGroupCode: price.priceGroup.id,
      price: price.price,
      surcharge: price.surcharge,
      pricePerMinute: price.minuteWage,
    };

    return input;
  });

  if (simplePrices.length > 0) {
    articlePricesRelation.simplePrices = simplePrices;
  }

  return Object.keys(articlePricesRelation).length > 0
    ? articlePricesRelation
    : null;
}

function mapLedgerAccountAssignmentsRelation(
  oldValues: ArticleLedgerAccount[],
  newValues: ArticleLedgerAccount[]
): InputArticlePrices {
  if (isEqual(oldValues, newValues)) {
    return null;
  }

  const relation: InputArticleLedgerAccountsAssignmentsRelation = {};

  const ledgerAccounts = newValues
    .map((la) => {
      const input: InputArticleSetLedgerAccountsAssignmentRelation = {
        taxGroupCode: la.taxGroup.id,
        accountNumber: la.ledgerAccount?.number ?? 0,
        vatCode: la.vatRate?.id ?? 0,
      };

      return input;
    })
    .filter((x) => x.accountNumber !== 0 || x.vatCode !== 0);

  relation.set = ledgerAccounts;

  return relation;
}
